import { Inject, Injectable } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Store } from '@ngrx/store';
import * as fromRoot from '../../app.reducer';
import * as UI from '../../shared/store/ui.actions';
import { Router } from '@angular/router';
import { UIService } from 'src/app/shared/store/ui.service';
import { Base64Service } from 'src/app/shared/services/base64.service';
import { AlunoService } from 'src/app/shared/services/aluno.service';
import { jwtDecode } from 'jwt-decode';
import moment from 'moment';
import { HttpService } from 'src/app/shared/services/http.service';
import { NavController } from '@ionic/angular';
import { HttpClient, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { catchError, map, Observable, of, Subject } from 'rxjs';
import { environment } from 'src/environments/environment';
import { Platform } from '@ionic/angular';
import { FcmService } from 'src/app/services/fcm.service';
import { CacheCleanerService } from 'src/app/services/cache-cleaner.service';
import { TransferenciaService } from 'src/app/pages/transferencia/transferencia.service';
import { REQUISICAO_PUBLICA } from 'src/app/shared/http/requisicao-publica';

interface JWTPayload {
    user_id: number;
    userId: string;
    username: string;
    exp: number;
}

interface ValidaTokenAlunoResponse {
    access_token?: string;
    message?: string;
}

@Injectable({
    providedIn: 'root',
})
export class AutenticacaoService {
    public _login: Subject<number> = new Subject<number>();

    constructor(
        private router: Router,
        private store: Store<fromRoot.State>,
        private uiService: UIService,
        private base64: Base64Service,
        private httpService: HttpService,
        private http: HttpClient,
        private navController: NavController,
        private alunoService: AlunoService,
        private platform: Platform,
        private fcmService: FcmService,
        private cacheCleanerService: CacheCleanerService,
        private transferenciaService: TransferenciaService,
        @Inject(DOCUMENT) private document: Document,
    ) {
        // Outra aba trocou o aluno da sessão (o localStorage é compartilhado): sem recarregar, esta
        // aba seguiria mostrando o aluno anterior enquanto busca dados com o token do novo.
        this.document.defaultView?.addEventListener('storage', (evento: StorageEvent) => {
            if (evento.key === 'token' && evento.newValue !== evento.oldValue) {
                this.document.location.reload();
            }
        });
    }

    login(authData: any) {
        this.store.dispatch(new UI.StartLoading());

        let data = {
            username: this.base64.encode(authData.username),
            password: this.base64.encode(authData.senha),
        };

        this.httpService.post('v1/auth/login', data).subscribe({
            next: (value): void => {
                if ((value as any).access_token) {
                    if (authData.username) {
                        localStorage.setItem('saved_re', authData.username);
                    }
                    this.setTokenSession((value as any).access_token);
                    this.store.dispatch(new UI.StopLoading());
                    this.router.navigate(['tabs/home']);
                    this._login.next(1);
                    this.initializeApp();

                    if ((value as any).default_pass === true) {
                        this.showDefaultPasswordAlert();
                    }
                } else {
                    this.store.dispatch(new UI.StopLoading());
                    this.uiService.presentToast('bottom', (value as any).message);
                }
            },
            error: (): void => {
                this.store.dispatch(new UI.StopLoading());
                this.router.navigate(['/entrar']);
                this.uiService.presentToast('bottom', 'Erro ao autenticar usuário.');
            },
        });
    }

    async showDefaultPasswordAlert() {
        await this.uiService.presentAlert({
            header: 'Alteração de Senha',
            message:
                'Para garantir a proteção dos seus dados e a conformidade com a política de segurança, é obrigatório realizar a alteração da senha neste momento.',
            backdropDismiss: true,
            buttons: [
                {
                    text: 'Alterar Senha',
                    cssClass: 'alert-button-confirm',
                    handler: () => {
                        this.router.navigate(['/tabs/seguranca/alterar-senha']);
                    },
                },
            ],
        });
    }

    initializeApp() {
        this.platform.ready().then(() => {
            this.fcmService.initPush();
        });
    }

    logout() {
        // Limpar todos os caches ANTES de limpar tokens/sessão
        this.cacheCleanerService.clearAllCaches();

        localStorage.removeItem('token');
        localStorage.removeItem('access_token');
        localStorage.removeItem('expires_at');
        localStorage.removeItem('turmaSelecionada');

        this.alunoService.limparDadosAluno();

        // A raiz do app é um ion-tabs: navegar para /entrar não destrói as telas já abertas, e o
        // próximo aluno a entrar veria os dados carregados pelo anterior. Recarregar começa do zero.
        this.document.location.replace('/entrar');
    }

    forceLogout(): void {
        this.logout();
    }

    validarInscricaoAluno(): Observable<boolean> {
        return this.transferenciaService.getAluno().pipe(
            map((response) => !!response?.data),
            catchError(() => of(false)),
        );
    }

    get token(): string | null {
        return localStorage.getItem('token')?.replace('Bearer ', '') ?? null;
    }

    setTokenSession(token: string, options?: { silent?: boolean }) {
        try {
            const payload = <JWTPayload>jwtDecode(token);
            const expiresAt = moment.unix(payload.exp);
            localStorage.setItem('token', 'Bearer ' + token);
            localStorage.setItem('access_token', token);
            localStorage.setItem('expires_at', JSON.stringify(expiresAt.valueOf()));
            // valid token format
        } catch (error) {
            // invalid token format
            localStorage.removeItem('token');
            localStorage.removeItem('access_token');
            localStorage.removeItem('expires_at');
            console.error('Token Inválido');
            this.alunoService.limparDadosAluno();

            if (!options?.silent) {
                this.navController.navigateRoot('/entrar', {
                    animated: true,
                    animationDirection: 'back',
                });
            }
        }
    }

    get usuarioId(): string | null {
        if (!this.isLoggedIn() || !this.token) {
            return null;
        }

        try {
            const payload = <JWTPayload>jwtDecode(this.token);
            return payload?.userId ?? null;
        } catch {
            return null;
        }
    }

    getExpiration() {
        const expiration = localStorage.getItem('expires_at');

        if (expiration) {
            const expiresAt = JSON.parse(expiration);
            return moment(expiresAt);
        } else {
            // Trate o caso em que 'expiresAt' não está definido
            return moment(0);
        }
    }

    isLoggedIn(): boolean {
        return !!this.token && moment().isBefore(this.getExpiration());
    }

    isLoggedOut() {
        return !this.isLoggedIn();
    }

    getBearerToken(): string {
        return this.getStoredAccessToken();
    }

    getStoredAccessToken(): string {
        return localStorage.getItem('access_token') ?? this.token?.replace('Bearer ', '') ?? '';
    }

    validaTokenAluno(): Observable<boolean> {
        const access_token = this.getStoredAccessToken();

        if (!access_token) {
            return of(false);
        }

        return this.http
            .post<ValidaTokenAlunoResponse>(`${environment.api}v1/auth/validaTokenAluno`, {
                access_token: access_token,
            }, {
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${access_token}`,
                },
            })
            .pipe(
                map((response) => {
                    if (response.access_token) {
                        this.setTokenSession(response.access_token, { silent: true });
                        this.initializeApp();
                        return true;
                    }
                    return false;
                }),
                catchError(() => of(false)),
            );
    }

}

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        const token = localStorage.getItem('token');

        if (token && !req.context.get(REQUISICAO_PUBLICA)) {
            const cloned = req.clone({
                headers: req.headers.set('Authorization', '' + token),
            });

            return next.handle(cloned);
        } else {
            return next.handle(req);
        }
    }
}
